import { useCallback } from 'react';
import { useResponsive } from '@sg/garnish';

import type { TelemetryEvent } from '@order/Telemetry';
import { telemetryLocationFromInput, useTelemetry } from '@order/Telemetry';

import type { LocationSearchMachineContext } from '../../machine';

// ─────────────────────────────────────────────────────────────────────────────

export const useLocationSearchTelemetry = () => {
  const { track } = useTelemetry();
  const { currentBreakpoint } = useResponsive();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const trackLocationSearchType = useCallback(
    (context: LocationSearchMachineContext) => {
      const { locationSearchType } = context;

      track('location.channel_changed', { channel: locationSearchType });
    },
    [track],
  );

  const trackLocationSearchByArea = useCallback(
    (context: LocationSearchMachineContext) => {
      const { locations, currentPosition } = context;
      const { pickup = [], outpost = [] } = locations ?? {};
      const hasLocations = pickup.length + outpost.length > 0;

      track('location.search', {
        searchMethod: 'area-button',
        resultsReturned: hasLocations,
        geolocationEnabled: currentPosition !== undefined,
      });
    },
    [track],
  );

  const trackLocationSearchByString = useCallback(
    (context: LocationSearchMachineContext) => {
      const { locations, currentPosition } = context;
      const { pickup = [], outpost = [] } = locations ?? {};
      const hasLocations = pickup.length + outpost.length > 0;

      track('location.search', {
        searchMethod: 'search-field',
        resultsReturned: hasLocations,
        geolocationEnabled: currentPosition !== undefined,
      });
    },
    [track],
  );

  const trackLocationSearchByDeliveryPlaceID = useCallback(
    (context: LocationSearchMachineContext) => {
      const { currentPosition, deliveryLocation } = context;
      const hasDeliveryLocation = deliveryLocation?.restaurantId !== undefined;

      if (!hasDeliveryLocation) {
        track('location.delivery_unavailable');

        return;
      }

      track('location.search', {
        searchMethod: 'search-field',
        resultsReturned: true,
        geolocationEnabled: currentPosition !== undefined,
      });
    },
    [track],
  );

  const trackFocusedLocationPinId = useCallback(
    (context: LocationSearchMachineContext) => {
      const { focusedPinId } = context;
      const isLocationPin = focusedPinId !== 'customer';

      if (!isLocationPin) return;

      track('location.pin_selected');
    },
    [track],
  );

  const trackNavigateToLocationMenu = useCallback(
    (entryPoint?: LocationNavigationEntryPoint) =>
      (context: LocationSearchMachineContext) => {
        track('location.open_menu', {
          location: telemetryLocationFromInput(context.activeLocation),
          bottomSheetOpen: currentBreakpoint.isMobile,
          entryPoint,
        });
      },
    [currentBreakpoint.isMobile, track],
  );

  const trackNavigateToDeliveryLocationMenu = useCallback(
    (entryPoint?: LocationNavigationEntryPoint) =>
      (context: LocationSearchMachineContext) => {
        track('location.open_menu', {
          location: telemetryLocationFromInput(context.activeLocation),
          bottomSheetOpen: currentBreakpoint.isMobile,
          entryPoint,
        });
      },
    [currentBreakpoint.isMobile, track],
  );

  const trackLocationResultsExpandCollapse = useCallback(
    (isExpanded: boolean) => {
      track('location.bottom_sheet', {
        state: isExpanded ? 'expanded' : 'collapsed',
      });
    },
    [track],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    trackLocationSearchByArea,
    trackLocationSearchByString,
    trackLocationSearchByDeliveryPlaceID,
    trackLocationResultsExpandCollapse,
    trackNavigateToLocationMenu,
    trackNavigateToDeliveryLocationMenu,
    trackLocationSearchType,
    trackFocusedLocationPinId,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationNavigationEntryPoint =
  TelemetryEvent<'location.open_menu'>['payload']['entryPoint'];
